import { format, isValid, parseISO } from 'date-fns';
import type { RefObject } from 'react';

import { useI18n } from '#/i18n/hooks/useI18n';
import { useLoadBundleCssOnce } from '#/zustand-stores/loadCssBundleStore';

import { DatePickerDateInput } from './DatePickerDateInput';
import { DatePickerPopperContainer } from './DatePickerPopperContainer';
import { ReactDatePickerWrapperLoadable } from './ReactDatePickerWrapperLoadable';
import { createLocalDateString } from './helpers/createLocalDateString';

type Props = {
  className?: string;
  id?: string;
  name?: string;
  required?: boolean;
  value: string | null | undefined;
  placeholder?: string;
  onChange: (date: string | null) => void;
  disablePastDates?: boolean;
  disableFutureDates?: boolean;
  strictParsing?: boolean;
  dateFormat?: string;
  isTimeZoneAware?: boolean;
  showMonthYearPicker?: boolean;
  qaId?: string;
  ref?: RefObject<HTMLInputElement | null>;
};

export function DatePicker({
  className,
  value,
  onChange,
  disablePastDates,
  disableFutureDates,
  strictParsing = true,
  dateFormat,
  isTimeZoneAware,
  showMonthYearPicker,
  qaId,
  ref,
  ...rest
}: Props) {
  const { locale } = useI18n();
  const datetimeProps = {
    ...rest,
    ref,
    qaId,
    autoComplete: 'off',
  };

  useLoadBundleCssOnce('reactDatepicker');

  const parsedValue = value ? parseISO(value) : null;
  return (
    <ReactDatePickerWrapperLoadable
      className={className}
      customInput={<DatePickerDateInput inputProps={datetimeProps} />}
      shouldCloseOnSelect
      locale={locale}
      name={datetimeProps.name}
      dateFormat={dateFormat ?? 'P'}
      showMonthYearPicker={showMonthYearPicker}
      minDate={disablePastDates ? new Date() : undefined}
      maxDate={disableFutureDates ? new Date() : undefined}
      popperContainer={DatePickerPopperContainer}
      strictParsing={strictParsing}
      selected={parsedValue}
      onChange={(selectedDate: Date | null) => {
        if (selectedDate && isValid(selectedDate) && isTimeZoneAware) {
          onChange(createLocalDateString(selectedDate));
        } else if (selectedDate && isValid(selectedDate)) {
          const dateString = format(selectedDate, 'yyyy-MM-dd');
          onChange(dateString);
        } else {
          onChange(null);
        }
      }}
    />
  );
}
